import {
    Directive,
    inject
} from "@angular/core";
import { EnhancedFormDirective } from "../../enhanced-form.directive";

/**
 * Attribute directive that wraps a filter form element and pre-configures
 * `EnhancedFormDirective` with `disableWhileProcessing` set to `false`.
 *
 * Filter forms (used to filter lists, dashboards, trees, or other components)
 * should remain interactive while the filtered component is loading. By composing
 * `EnhancedFormDirective` via `hostDirectives` and forcing
 * `disableWhileProcessing = false` at construction time, this directive ensures
 * the form is never disabled during application processing — while still exposing
 * all other `EnhancedFormDirective` capabilities (change tracking, validity state,
 * `canSubmit$`, `populate()`, etc.) through the normal `mgdEnhancedForm` exportAs.
 *
 * @example
 * ```html
 * <form mgdFilterForm [formGroup]="filterForm">
 *   <mat-form-field>
 *     <mat-label>Search</mat-label>
 *     <input matInput [formControlName]="'query'" />
 *   </mat-form-field>
 * </form>
 * ```
 *
 * @selector form[mgdFilterForm]
 */
@Directive({
    selector: "form[mgdFilterForm]",
    host: {
        class: "mgd-filter-form"
    },
    hostDirectives: [
        {
            directive: EnhancedFormDirective,
            inputs: [
                "formGroup",
                "canSubmitWithoutChanges"
            ]
        }
    ]
})
export class FilterFormDirective {

    /**
     * The composed `EnhancedFormDirective` instance, pre-configured with
     * `disableWhileProcessing = false`. Exposed so that component classes can
     * access the full `EnhancedFormDirective` API (`canSubmit$`, `isChanged$`,
     * `populate()`, `reset()`, etc.) via a `@ViewChild(FilterFormDirective)`
     * reference:
     *
     * ```typescript
     * @ViewChild(FilterFormDirective) filterForm: FilterFormDirective;
     *
     * // then:
     * this.filterForm.enhancedForm.canSubmit$
     * ```
     */
    readonly enhancedForm = inject(EnhancedFormDirective, { self: true });

    constructor() {
        // Set disableWhileProcessing=false immediately in the constructor so
        // EnhancedFormDirective.ngOnInit() (which subscribes to processing$)
        // reads the correct value when it first fires.
        this.enhancedForm.disableWhileProcessing = false;
    }
}
